import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { formatMoneySigned, formatPctSigned } from '../../utils/lotValue';
import { scatterDomain } from '../../utils/branchView';
import { useDealershipSettings } from '../../contexts/settingsContext';

const W = 420;
const H = 300;
const L = 44;
const R = 16;
const T = 20;
const B = 38;

const pctLabel = (v) => `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v)}%`;

export const dotLabel = (r, on, oldest = 180) => `${r.name}: ${r.agedPct.toFixed(0)}% past ${oldest} days, asking ${formatPctSigned(r.gapPct)} vs AI, ${r.units.toLocaleString('en-US')} units${on ? ', selected' : ''}`;

/**
 * B4 "Where the problems sit": share past 180 days (x) against the asking vs
 * AI gap (y), bubble area by units, the dealership as dashed lines, the
 * "older and over AI" corner shaded. The axes are computed from the data
 * (utils/branchView scatterDomain), so no branch is clipped. Each dot is a
 * button: click, Enter or Space toggles the branch; its tip shows on hover
 * and on keyboard focus. Branches without priced used units cannot be
 * placed and are named under the chart; a hidden table carries the data.
 */
export default function ProblemScatter({
    rows, dealership, selected, onToggle, standOutOnly,
}) {
    const [tip, setTip] = useState(null);
    // The share past the dealership's last aging boundary (180 by default).
    const oldest = useDealershipSettings().aging.boundaries[2];
    const plotted = rows.filter((r) => r.gapPct != null);
    const missing = rows.filter((r) => r.gapPct == null);
    const ref = {
        x: dealership?.agedPct ?? 0,
        y: dealership?.gapPct != null ? dealership.gapPct * 100 : 0,
    };
    const dom = scatterDomain(plotted.map((r) => ({ x: r.agedPct, y: r.gapPct * 100 })), ref);
    const maxUnits = Math.max(1, ...plotted.map((r) => r.units));
    const radius = (units) => 5 + 13 * Math.sqrt(units / maxUnits);
    // The plot area is inset by the largest bubble (plus its ring), so a
    // bubble at the edge of the domain is never clipped (QA BR-5).
    const inset = radius(maxUnits) + 4;
    const X = (v) => L + inset + ((v - dom.xMin) / (dom.xMax - dom.xMin || 1)) * (W - L - R - 2 * inset);
    const Y = (v) => T + inset + ((dom.yMax - v) / (dom.yMax - dom.yMin || 1)) * (H - T - B - 2 * inset);

    const toggle = (name) => onToggle(name);
    const onKeyDown = (name) => (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle(name);
        }
    };
    const showTip = (r) => () => setTip(r.name);
    const hideTip = () => setTip(null);
    const tipRow = plotted.find((r) => r.name === tip);

    return (
        <section className="v2-board br2-board br2-scat-card" aria-labelledby="br2-scat-h">
            <div className="br2-card-h">
                <h2 id="br2-scat-h">Where the problems sit</h2>
                <span className="br2-sub">Share past {oldest} days against the AI gap; bubble size is units. Click a dot, or focus it and press Enter, to select.</span>
            </div>
            <div className="br2-scat">
                {plotted.length === 0 ? (
                    <p className="br2-scat-empty">No branch has used units with both an asking price and an AI price to place.</p>
                ) : (
                    <svg viewBox={`0 0 ${W} ${H}`} role="group" aria-label={`Branches by share past ${oldest} days and AI gap`} data-testid="scatter">
                        <rect
                            className="br2-q"
                            x={X(ref.x)}
                            y={Y(dom.yMax)}
                            width={Math.max(0, X(dom.xMax) - X(ref.x))}
                            height={Math.max(0, Y(ref.y) - Y(dom.yMax))}
                            rx="4"
                        />
                        <text className="br2-ql" x={X(dom.xMax) - 6} y={Y(dom.yMax) + 14} textAnchor="end">OLDER AND OVER AI</text>
                        {dom.yTicks.map((v) => (
                            <g key={`y${v}`}>
                                <line className={v === 0 ? 'br2-gl br2-gl--zero' : 'br2-gl'} x1={L} x2={W - R} y1={Y(v)} y2={Y(v)} />
                                <text className="br2-ax" x={L - 6} y={Y(v) + 3.5} textAnchor="end">{pctLabel(v)}</text>
                            </g>
                        ))}
                        {dom.xTicks.map((v) => (
                            <text key={`x${v}`} className="br2-ax" x={X(v)} y={H - B + 16} textAnchor="middle">{v}%</text>
                        ))}
                        <text className="br2-ax" x={W - R} y={H - 4} textAnchor="end">Units past {oldest} days →</text>
                        <text className="br2-ax br2-ax--b" x={L} y={T - 6} textAnchor="start">↑ Asking above AI</text>
                        <line className="br2-avgl" x1={X(ref.x)} x2={X(ref.x)} y1={T} y2={H - B} />
                        <line className="br2-avgl" x1={L} x2={W - R} y1={Y(ref.y)} y2={Y(ref.y)} />
                        {[...plotted].sort((a, b) => b.units - a.units).map((r) => {
                            const on = selected.has(r.name);
                            const cx = X(r.agedPct);
                            const cy = Y(r.gapPct * 100);
                            const rad = radius(r.units);
                            const faded = standOutOnly && !r.standOut;
                            return (
                                <g
                                    key={r.name}
                                    className={`br2-pt${faded ? ' is-faded' : ''}`}
                                    role="button"
                                    tabIndex={0}
                                    aria-pressed={on}
                                    aria-label={dotLabel(r, false, oldest)}
                                    data-pt={r.name}
                                    onClick={() => toggle(r.name)}
                                    onKeyDown={onKeyDown(r.name)}
                                    onMouseEnter={showTip(r)}
                                    onMouseLeave={hideTip}
                                    onFocus={showTip(r)}
                                    onBlur={hideTip}
                                >
                                    <circle className={`br2-pt-d br2-pt-d--${r.flags.gap}`} cx={cx} cy={cy} r={rad} />
                                    <circle className="br2-pt-ring" cx={cx} cy={cy} r={rad + 3.5} />
                                </g>
                            );
                        })}
                        {plotted.filter((r) => r.standOut).map((r) => (
                            <text key={`l${r.name}`} className="br2-pl" x={X(r.agedPct) + radius(r.units) + 5} y={Y(r.gapPct * 100) + 4}>{r.name}</text>
                        ))}
                    </svg>
                )}
                {tipRow && (
                    <div
                        className="br2-scat-tip"
                        aria-hidden="true"
                        data-testid="scatter-tip"
                        style={{ left: `${(X(tipRow.agedPct) / W) * 100}%`, top: `${(Y(tipRow.gapPct * 100) / H) * 100}%` }}
                    >
                        <b>{tipRow.name}</b> · {tipRow.units.toLocaleString('en-US')} units<br />
                        Past {oldest} days {tipRow.agedPct.toFixed(0)}% (dealer {ref.x.toFixed(0)}%)<br />
                        Asking {formatPctSigned(tipRow.gapPct)} vs AI · {formatMoneySigned(tipRow.gapTotal)}<br />
                        High risk {tipRow.riskPct.toFixed(0)}% of scored
                    </div>
                )}
            </div>
            <div className="br2-legend br2-legend--pad">
                <span><i className="br2-dot v2-tone-red" aria-hidden="true" />Gap over +10%</span>
                <span><i className="br2-dot v2-tone-amber" aria-hidden="true" />+5 to +10%</span>
                <span><i className="br2-dot v2-tone-sage" aria-hidden="true" />Within ±5%</span>
                <span><i className="br2-dot v2-tone-teal" aria-hidden="true" />Under −5%</span>
                <span><i className="br2-avgkey" aria-hidden="true" />Dealership average</span>
            </div>
            {missing.length > 0 && (
                <p className="br2-cnote" data-testid="scatter-missing">
                    Not placed (no used units with both an asking and an AI price): {missing.map((r) => r.name).join(', ')}.
                </p>
            )}
            <table className="v2-sr">
                <caption>{`Branches by share past ${oldest} days and asking vs AI gap`}</caption>
                <thead><tr><th scope="col">Branch</th><th scope="col">{`Past ${oldest} days`}</th><th scope="col">Asking vs AI</th><th scope="col">Units</th></tr></thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.name}>
                            <th scope="row">{r.name}</th>
                            <td>{r.agedPct.toFixed(0)}%</td>
                            <td>{r.gapPct == null ? 'no AI comparison' : formatPctSigned(r.gapPct)}</td>
                            <td>{r.units}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </section>
    );
}

ProblemScatter.propTypes = {
    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
    dealership: PropTypes.object,
    selected: PropTypes.instanceOf(Set).isRequired,
    onToggle: PropTypes.func.isRequired,
    standOutOnly: PropTypes.bool.isRequired,
};
